import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Trash2, Eye, Play, Pause, RefreshCw, Key, Copy, CheckCircle,
  AlertCircle, Clock, Settings, ChevronDown, ChevronRight, Link2,
  Activity, Database, Table, X, Shield, Zap, ArrowRight, Info,
  Search, Package, ArrowLeftRight, Lock, Unlock, FileText, BookOpen,
  LayoutTemplate, Check, ExternalLink, ChevronUp, Layers, MapPin,
  AlertTriangle, Save, RotateCcw
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';
import { ClassificationMappingEditor } from './ClassificationMappingEditor';
import CAMPAIGN_API_SOURCE_TEMPLATES, {
  isCampaignTemplate,
} from './campaign_api_templates';

// ==================== TEMPLATE REGISTRY ====================

const API_SOURCE_TEMPLATES = {
  // ── CRM & Sales Systems ──
  crm_sales: {
    category: 'CRM & Sales',
    icon: '💼',
    templates: [
      {
        id: 'salesforce_contacts',
        name: 'Salesforce – Contacts',
        description: 'Standard Salesforce contact records with lifecycle tracking',
        provider: 'Salesforce',
        logo: '☁️',
        data_intent: 'customer_profile',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 5000,
        primary_key_columns: ['sf_contact_id'],
        column_schema: [
          { name: 'sf_contact_id', data_type: 'string', required: true, description: 'Salesforce Contact ID (18-char)' },
          { name: 'email', data_type: 'string', required: true, description: 'Primary email' },
          { name: 'first_name', data_type: 'string', required: false },
          { name: 'last_name', data_type: 'string', required: false },
          { name: 'phone', data_type: 'string', required: false },
          { name: 'mobile_phone', data_type: 'string', required: false },
          { name: 'company', data_type: 'string', required: false, description: 'Account / Company name' },
          { name: 'title', data_type: 'string', required: false, description: 'Job title' },
          { name: 'department', data_type: 'string', required: false },
          { name: 'lead_source', data_type: 'string', required: false },
          { name: 'lifecycle_stage', data_type: 'string', required: false, description: 'Lead / MQL / SQL / Customer' },
          { name: 'owner_name', data_type: 'string', required: false, description: 'Assigned sales rep' },
          { name: 'created_date', data_type: 'datetime', required: false },
          { name: 'last_activity_date', data_type: 'datetime', required: false },
          { name: 'annual_revenue', data_type: 'float', required: false },
          { name: 'industry', data_type: 'string', required: false },
          { name: 'mailing_city', data_type: 'string', required: false },
          { name: 'mailing_country', data_type: 'string', required: false },
        ],
      },
      {
        id: 'salesforce_opportunities',
        name: 'Salesforce – Opportunities',
        description: 'Deal pipeline with stage tracking and revenue data',
        provider: 'Salesforce',
        logo: '☁️',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 5000,
        primary_key_columns: ['opportunity_id'],
        column_schema: [
          { name: 'opportunity_id', data_type: 'string', required: true },
          { name: 'sf_contact_id', data_type: 'string', required: false, description: 'Linked contact' },
          { name: 'email', data_type: 'string', required: false },
          { name: 'opportunity_name', data_type: 'string', required: false },
          { name: 'stage_name', data_type: 'string', required: false },
          { name: 'amount', data_type: 'float', required: false },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'close_date', data_type: 'date', required: false },
          { name: 'probability', data_type: 'float', required: false },
          { name: 'type', data_type: 'string', required: false, description: 'New Business / Renewal' },
          { name: 'lead_source', data_type: 'string', required: false },
          { name: 'owner_name', data_type: 'string', required: false },
          { name: 'created_date', data_type: 'datetime', required: false },
          { name: 'last_modified_date', data_type: 'datetime', required: false },
          { name: 'is_won', data_type: 'bool', required: false },
          { name: 'is_closed', data_type: 'bool', required: false },
        ],
        column_roles: { customer_key: 'sf_contact_id', event_type: 'stage_name', event_value: 'amount', event_timestamp: 'last_modified_date' },
      },
      {
        id: 'hubspot_contacts',
        name: 'HubSpot – Contacts',
        description: 'HubSpot CRM contact properties and marketing data',
        provider: 'HubSpot',
        logo: '🟠',
        data_intent: 'customer_profile',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 5000,
        primary_key_columns: ['hs_contact_id'],
        column_schema: [
          { name: 'hs_contact_id', data_type: 'string', required: true },
          { name: 'email', data_type: 'string', required: true },
          { name: 'first_name', data_type: 'string', required: false },
          { name: 'last_name', data_type: 'string', required: false },
          { name: 'phone', data_type: 'string', required: false },
          { name: 'company', data_type: 'string', required: false },
          { name: 'job_title', data_type: 'string', required: false },
          { name: 'lifecycle_stage', data_type: 'string', required: false },
          { name: 'lead_status', data_type: 'string', required: false },
          { name: 'hs_lead_source', data_type: 'string', required: false },
          { name: 'owner_name', data_type: 'string', required: false },
          { name: 'city', data_type: 'string', required: false },
          { name: 'country', data_type: 'string', required: false },
          { name: 'create_date', data_type: 'datetime', required: false },
          { name: 'last_modified_date', data_type: 'datetime', required: false },
          { name: 'num_associated_deals', data_type: 'int', required: false },
          { name: 'total_revenue', data_type: 'float', required: false },
        ],
      },
      {
        id: 'dynamics_contacts',
        name: 'Microsoft Dynamics 365 – Contacts',
        description: 'Dynamics CRM contact entities with activity tracking',
        provider: 'Microsoft Dynamics',
        logo: '🔷',
        data_intent: 'customer_profile',
        merge_mode: 'auto',
        merge_interval_seconds: 600,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['dynamics_contact_id'],
        column_schema: [
          { name: 'dynamics_contact_id', data_type: 'string', required: true, description: 'Dynamics GUID' },
          { name: 'email_address1', data_type: 'string', required: true },
          { name: 'first_name', data_type: 'string', required: false },
          { name: 'last_name', data_type: 'string', required: false },
          { name: 'telephone1', data_type: 'string', required: false },
          { name: 'company_name', data_type: 'string', required: false },
          { name: 'job_title', data_type: 'string', required: false },
          { name: 'department', data_type: 'string', required: false },
          { name: 'address1_city', data_type: 'string', required: false },
          { name: 'address1_country', data_type: 'string', required: false },
          { name: 'status_code', data_type: 'string', required: false, description: 'Active / Inactive' },
          { name: 'owner_name', data_type: 'string', required: false },
          { name: 'created_on', data_type: 'datetime', required: false },
          { name: 'modified_on', data_type: 'datetime', required: false },
          { name: 'annual_income', data_type: 'float', required: false },
          { name: 'lead_source_code', data_type: 'string', required: false },
        ],
      },
      {
        id: 'pipedrive_deals',
        name: 'Pipedrive – Deals',
        description: 'Pipedrive deal pipeline with custom fields',
        provider: 'Pipedrive',
        logo: '🟢',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['pipedrive_deal_id'],
        column_schema: [
          { name: 'pipedrive_deal_id', data_type: 'string', required: true },
          { name: 'person_email', data_type: 'string', required: false },
          { name: 'person_name', data_type: 'string', required: false },
          { name: 'org_name', data_type: 'string', required: false },
          { name: 'deal_title', data_type: 'string', required: false },
          { name: 'pipeline_name', data_type: 'string', required: false },
          { name: 'stage_name', data_type: 'string', required: false },
          { name: 'deal_value', data_type: 'float', required: false },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false, description: 'open / won / lost' },
          { name: 'expected_close_date', data_type: 'date', required: false },
          { name: 'add_time', data_type: 'datetime', required: false },
          { name: 'update_time', data_type: 'datetime', required: false },
          { name: 'won_time', data_type: 'datetime', required: false },
          { name: 'owner_name', data_type: 'string', required: false },
        ],
        column_roles: { customer_key: 'person_email', event_type: 'stage_name', event_value: 'deal_value', event_timestamp: 'update_time' },
      },
    ],
  },

  // ── Customer Support Systems ──
  support: {
    category: 'Customer Support',
    icon: '🎧',
    templates: [
      {
        id: 'zendesk_tickets',
        name: 'Zendesk – Tickets',
        description: 'Support ticket lifecycle data with SLA tracking',
        provider: 'Zendesk',
        logo: '🟩',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 5000,
        primary_key_columns: ['ticket_id'],
        column_schema: [
          { name: 'ticket_id', data_type: 'string', required: true },
          { name: 'requester_email', data_type: 'string', required: true },
          { name: 'requester_name', data_type: 'string', required: false },
          { name: 'subject', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false, description: 'new / open / pending / solved / closed' },
          { name: 'priority', data_type: 'string', required: false, description: 'low / normal / high / urgent' },
          { name: 'ticket_type', data_type: 'string', required: false, description: 'question / incident / problem / task' },
          { name: 'channel', data_type: 'string', required: false, description: 'email / chat / phone / web' },
          { name: 'group_name', data_type: 'string', required: false },
          { name: 'assignee_name', data_type: 'string', required: false },
          { name: 'tags', data_type: 'string', required: false, description: 'Comma-separated tags' },
          { name: 'satisfaction_rating', data_type: 'string', required: false },
          { name: 'first_response_minutes', data_type: 'int', required: false },
          { name: 'resolution_minutes', data_type: 'int', required: false },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'updated_at', data_type: 'datetime', required: false },
          { name: 'solved_at', data_type: 'datetime', required: false },
        ],
        column_roles: { customer_key: 'requester_email', event_type: 'status', event_value: 'priority', event_timestamp: 'updated_at' },
      },
      {
        id: 'freshdesk_tickets',
        name: 'Freshdesk – Tickets',
        description: 'Freshdesk helpdesk tickets with conversation metrics',
        provider: 'Freshdesk',
        logo: '🌿',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['fd_ticket_id'],
        column_schema: [
          { name: 'fd_ticket_id', data_type: 'string', required: true },
          { name: 'requester_email', data_type: 'string', required: true },
          { name: 'requester_name', data_type: 'string', required: false },
          { name: 'subject', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false, description: 'Open / Pending / Resolved / Closed' },
          { name: 'priority', data_type: 'string', required: false },
          { name: 'ticket_type', data_type: 'string', required: false },
          { name: 'source', data_type: 'string', required: false, description: 'Email / Portal / Phone / Chat' },
          { name: 'group_name', data_type: 'string', required: false },
          { name: 'agent_name', data_type: 'string', required: false },
          { name: 'tags', data_type: 'string', required: false },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'updated_at', data_type: 'datetime', required: false },
          { name: 'resolved_at', data_type: 'datetime', required: false },
          { name: 'first_responded_at', data_type: 'datetime', required: false },
          { name: 'nr_replies', data_type: 'int', required: false },
        ],
        column_roles: { customer_key: 'requester_email', event_type: 'status', event_value: 'priority', event_timestamp: 'updated_at' },
      },
      {
        id: 'intercom_conversations',
        name: 'Intercom – Conversations',
        description: 'Intercom chat conversations and user engagement events',
        provider: 'Intercom',
        logo: '💬',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['conversation_id'],
        column_schema: [
          { name: 'conversation_id', data_type: 'string', required: true },
          { name: 'user_email', data_type: 'string', required: true },
          { name: 'user_name', data_type: 'string', required: false },
          { name: 'conversation_type', data_type: 'string', required: false },
          { name: 'state', data_type: 'string', required: false, description: 'open / closed / snoozed' },
          { name: 'source_type', data_type: 'string', required: false, description: 'conversation / push / email' },
          { name: 'assignee_name', data_type: 'string', required: false },
          { name: 'team_name', data_type: 'string', required: false },
          { name: 'tags', data_type: 'string', required: false },
          { name: 'rating', data_type: 'int', required: false },
          { name: 'rating_remark', data_type: 'string', required: false },
          { name: 'first_contact_reply_minutes', data_type: 'int', required: false },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'updated_at', data_type: 'datetime', required: false },
        ],
        column_roles: { customer_key: 'user_email', event_type: 'state', event_value: 'rating', event_timestamp: 'updated_at' },
      },
    ],
  },

  // ── Website & Product Analytics ──
  analytics: {
    category: 'Website & Product Analytics',
    icon: '📊',
    templates: [
      {
        id: 'google_analytics_events',
        name: 'Google Analytics 4 – Events',
        description: 'GA4 event stream with user properties and session data',
        provider: 'Google Analytics',
        logo: '📈',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 600,
        rate_limit_per_minute: 10000,
        primary_key_columns: ['event_id'],
        column_schema: [
          { name: 'event_id', data_type: 'string', required: true, description: 'Unique event identifier' },
          { name: 'client_id', data_type: 'string', required: false, description: 'GA client ID / cookie ID' },
          { name: 'user_id', data_type: 'string', required: false, description: 'Logged-in user identifier' },
          { name: 'email', data_type: 'string', required: false, description: 'If available via user property' },
          { name: 'event_name', data_type: 'string', required: true, description: 'page_view / purchase / sign_up' },
          { name: 'event_timestamp', data_type: 'datetime', required: true },
          { name: 'session_id', data_type: 'string', required: false },
          { name: 'page_location', data_type: 'string', required: false },
          { name: 'page_title', data_type: 'string', required: false },
          { name: 'page_referrer', data_type: 'string', required: false },
          { name: 'source', data_type: 'string', required: false, description: 'Traffic source' },
          { name: 'medium', data_type: 'string', required: false, description: 'Traffic medium' },
          { name: 'campaign', data_type: 'string', required: false },
          { name: 'device_category', data_type: 'string', required: false, description: 'desktop / mobile / tablet' },
          { name: 'country', data_type: 'string', required: false },
          { name: 'city', data_type: 'string', required: false },
          { name: 'event_value', data_type: 'float', required: false },
          { name: 'items', data_type: 'string', required: false, description: 'JSON array of items (ecommerce)' },
        ],
        column_roles: { customer_key: 'user_id', event_type: 'event_name', event_value: 'event_value', event_timestamp: 'event_timestamp' },
      },
      {
        id: 'mixpanel_events',
        name: 'Mixpanel – Events',
        description: 'Product analytics events with user and session properties',
        provider: 'Mixpanel',
        logo: '🟣',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 10000,
        primary_key_columns: ['insert_id'],
        column_schema: [
          { name: 'insert_id', data_type: 'string', required: true, description: 'Mixpanel dedup ID' },
          { name: 'distinct_id', data_type: 'string', required: true, description: 'User identifier' },
          { name: 'email', data_type: 'string', required: false },
          { name: 'event_name', data_type: 'string', required: true },
          { name: 'event_time', data_type: 'datetime', required: true },
          { name: 'session_id', data_type: 'string', required: false },
          { name: 'current_url', data_type: 'string', required: false },
          { name: 'referrer', data_type: 'string', required: false },
          { name: 'utm_source', data_type: 'string', required: false },
          { name: 'utm_medium', data_type: 'string', required: false },
          { name: 'utm_campaign', data_type: 'string', required: false },
          { name: 'os', data_type: 'string', required: false },
          { name: 'browser', data_type: 'string', required: false },
          { name: 'device', data_type: 'string', required: false },
          { name: 'country_code', data_type: 'string', required: false },
          { name: 'city', data_type: 'string', required: false },
          { name: 'event_properties', data_type: 'string', required: false, description: 'JSON custom properties' },
        ],
        column_roles: { customer_key: 'distinct_id', event_type: 'event_name', event_value: '', event_timestamp: 'event_time' },
      },
      {
        id: 'segment_track',
        name: 'Segment – Track Events',
        description: 'Segment track API compatible schema for user event streaming',
        provider: 'Segment',
        logo: '🟢',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 10000,
        primary_key_columns: ['message_id'],
        column_schema: [
          { name: 'message_id', data_type: 'string', required: true },
          { name: 'user_id', data_type: 'string', required: false },
          { name: 'anonymous_id', data_type: 'string', required: false },
          { name: 'email', data_type: 'string', required: false },
          { name: 'event', data_type: 'string', required: true },
          { name: 'timestamp', data_type: 'datetime', required: true },
          { name: 'context_page_url', data_type: 'string', required: false },
          { name: 'context_page_title', data_type: 'string', required: false },
          { name: 'context_page_referrer', data_type: 'string', required: false },
          { name: 'context_campaign_source', data_type: 'string', required: false },
          { name: 'context_campaign_medium', data_type: 'string', required: false },
          { name: 'context_campaign_name', data_type: 'string', required: false },
          { name: 'context_device_type', data_type: 'string', required: false },
          { name: 'context_os_name', data_type: 'string', required: false },
          { name: 'context_locale', data_type: 'string', required: false },
          { name: 'properties', data_type: 'string', required: false, description: 'JSON event properties' },
        ],
        column_roles: { customer_key: 'user_id', event_type: 'event', event_value: '', event_timestamp: 'timestamp' },
      },
      {
        id: 'amplitude_events',
        name: 'Amplitude – Events',
        description: 'Amplitude product analytics event data',
        provider: 'Amplitude',
        logo: '🔵',
        data_intent: 'event_stream',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 10000,
        primary_key_columns: ['event_id'],
        column_schema: [
          { name: 'event_id', data_type: 'string', required: true },
          { name: 'user_id', data_type: 'string', required: false },
          { name: 'device_id', data_type: 'string', required: false },
          { name: 'email', data_type: 'string', required: false },
          { name: 'event_type', data_type: 'string', required: true },
          { name: 'event_time', data_type: 'datetime', required: true },
          { name: 'session_id', data_type: 'string', required: false },
          { name: 'platform', data_type: 'string', required: false },
          { name: 'os_name', data_type: 'string', required: false },
          { name: 'device_type', data_type: 'string', required: false },
          { name: 'country', data_type: 'string', required: false },
          { name: 'city', data_type: 'string', required: false },
          { name: 'app_version', data_type: 'string', required: false },
          { name: 'event_properties', data_type: 'string', required: false, description: 'JSON event properties' },
          { name: 'user_properties', data_type: 'string', required: false, description: 'JSON user properties' },
        ],
        column_roles: { customer_key: 'user_id', event_type: 'event_type', event_value: '', event_timestamp: 'event_time' },
      },
    ],
  },

  // ── eCommerce Platforms ──
  ecommerce: {
    category: 'eCommerce',
    icon: '🛒',
    templates: [
      {
        id: 'shopify_orders',
        name: 'Shopify – Orders',
        description: 'Shopify order data with line items and fulfillment status',
        provider: 'Shopify',
        logo: '🛍️',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 5000,
        primary_key_columns: ['order_id'],
        column_schema: [
          { name: 'order_id', data_type: 'string', required: true },
          { name: 'order_number', data_type: 'string', required: false },
          { name: 'email', data_type: 'string', required: true },
          { name: 'customer_id', data_type: 'string', required: false },
          { name: 'first_name', data_type: 'string', required: false },
          { name: 'last_name', data_type: 'string', required: false },
          { name: 'phone', data_type: 'string', required: false },
          { name: 'total_price', data_type: 'float', required: false },
          { name: 'subtotal_price', data_type: 'float', required: false },
          { name: 'total_tax', data_type: 'float', required: false },
          { name: 'total_discounts', data_type: 'float', required: false },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'financial_status', data_type: 'string', required: false, description: 'paid / pending / refunded' },
          { name: 'fulfillment_status', data_type: 'string', required: false, description: 'fulfilled / partial / null' },
          { name: 'line_items_count', data_type: 'int', required: false },
          { name: 'line_items_json', data_type: 'string', required: false, description: 'JSON array of SKUs & quantities' },
          { name: 'shipping_city', data_type: 'string', required: false },
          { name: 'shipping_country', data_type: 'string', required: false },
          { name: 'tags', data_type: 'string', required: false },
          { name: 'source_name', data_type: 'string', required: false, description: 'web / pos / api' },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'processed_at', data_type: 'datetime', required: false },
          { name: 'cancelled_at', data_type: 'datetime', required: false },
        ],
        column_roles: { customer_key: 'email', event_type: 'financial_status', event_value: 'total_price', event_timestamp: 'created_at' },
      },
      {
        id: 'shopify_customers',
        name: 'Shopify – Customers',
        description: 'Shopify customer profiles with order history metrics',
        provider: 'Shopify',
        logo: '🛍️',
        data_intent: 'customer_profile',
        merge_mode: 'auto',
        merge_interval_seconds: 600,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['shopify_customer_id'],
        column_schema: [
          { name: 'shopify_customer_id', data_type: 'string', required: true },
          { name: 'email', data_type: 'string', required: true },
          { name: 'first_name', data_type: 'string', required: false },
          { name: 'last_name', data_type: 'string', required: false },
          { name: 'phone', data_type: 'string', required: false },
          { name: 'orders_count', data_type: 'int', required: false },
          { name: 'total_spent', data_type: 'float', required: false },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'verified_email', data_type: 'bool', required: false },
          { name: 'accepts_marketing', data_type: 'bool', required: false },
          { name: 'tags', data_type: 'string', required: false },
          { name: 'city', data_type: 'string', required: false },
          { name: 'country', data_type: 'string', required: false },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'updated_at', data_type: 'datetime', required: false },
          { name: 'last_order_id', data_type: 'string', required: false },
          { name: 'last_order_name', data_type: 'string', required: false },
        ],
      },
      {
        id: 'magento_orders',
        name: 'Magento / Adobe Commerce – Orders',
        description: 'Magento order data with product and payment details',
        provider: 'Magento',
        logo: '🧡',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['order_entity_id'],
        column_schema: [
          { name: 'order_entity_id', data_type: 'string', required: true },
          { name: 'increment_id', data_type: 'string', required: false, description: 'Human-readable order #' },
          { name: 'customer_email', data_type: 'string', required: true },
          { name: 'customer_id', data_type: 'string', required: false },
          { name: 'customer_firstname', data_type: 'string', required: false },
          { name: 'customer_lastname', data_type: 'string', required: false },
          { name: 'grand_total', data_type: 'float', required: false },
          { name: 'subtotal', data_type: 'float', required: false },
          { name: 'tax_amount', data_type: 'float', required: false },
          { name: 'discount_amount', data_type: 'float', required: false },
          { name: 'shipping_amount', data_type: 'float', required: false },
          { name: 'order_currency_code', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false },
          { name: 'state', data_type: 'string', required: false },
          { name: 'total_item_count', data_type: 'int', required: false },
          { name: 'items_json', data_type: 'string', required: false },
          { name: 'payment_method', data_type: 'string', required: false },
          { name: 'shipping_method', data_type: 'string', required: false },
          { name: 'store_name', data_type: 'string', required: false },
          { name: 'created_at', data_type: 'datetime', required: false },
          { name: 'updated_at', data_type: 'datetime', required: false },
        ],
        column_roles: { customer_key: 'customer_email', event_type: 'status', event_value: 'grand_total', event_timestamp: 'created_at' },
      },
      {
        id: 'woocommerce_orders',
        name: 'WooCommerce – Orders',
        description: 'WooCommerce order data with billing and line item details',
        provider: 'WooCommerce',
        logo: '🟣',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 300,
        rate_limit_per_minute: 3000,
        primary_key_columns: ['wc_order_id'],
        column_schema: [
          { name: 'wc_order_id', data_type: 'string', required: true },
          { name: 'order_number', data_type: 'string', required: false },
          { name: 'billing_email', data_type: 'string', required: true },
          { name: 'billing_first_name', data_type: 'string', required: false },
          { name: 'billing_last_name', data_type: 'string', required: false },
          { name: 'billing_phone', data_type: 'string', required: false },
          { name: 'total', data_type: 'float', required: false },
          { name: 'subtotal', data_type: 'float', required: false },
          { name: 'total_tax', data_type: 'float', required: false },
          { name: 'discount_total', data_type: 'float', required: false },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false, description: 'processing / completed / on-hold / refunded' },
          { name: 'payment_method_title', data_type: 'string', required: false },
          { name: 'line_items_count', data_type: 'int', required: false },
          { name: 'line_items_json', data_type: 'string', required: false },
          { name: 'shipping_city', data_type: 'string', required: false },
          { name: 'shipping_country', data_type: 'string', required: false },
          { name: 'customer_note', data_type: 'string', required: false },
          { name: 'date_created', data_type: 'datetime', required: false },
          { name: 'date_completed', data_type: 'datetime', required: false },
        ],
        column_roles: { customer_key: 'billing_email', event_type: 'status', event_value: 'total', event_timestamp: 'date_created' },
      },
      {
        id: 'stripe_payments',
        name: 'Stripe – Payments',
        description: 'Stripe payment intents and charge data',
        provider: 'Stripe',
        logo: '💳',
        data_intent: 'transaction',
        merge_mode: 'auto',
        merge_interval_seconds: 60,
        rate_limit_per_minute: 10000,
        primary_key_columns: ['payment_intent_id'],
        column_schema: [
          { name: 'payment_intent_id', data_type: 'string', required: true },
          { name: 'charge_id', data_type: 'string', required: false },
          { name: 'customer_id', data_type: 'string', required: false, description: 'Stripe customer ID' },
          { name: 'customer_email', data_type: 'string', required: false },
          { name: 'customer_name', data_type: 'string', required: false },
          { name: 'amount', data_type: 'float', required: true, description: 'Amount in cents/smallest currency unit' },
          { name: 'currency', data_type: 'string', required: false },
          { name: 'status', data_type: 'string', required: false, description: 'succeeded / failed / pending' },
          { name: 'payment_method_type', data_type: 'string', required: false },
          { name: 'description', data_type: 'string', required: false },
          { name: 'metadata_json', data_type: 'string', required: false },
          { name: 'invoice_id', data_type: 'string', required: false },
          { name: 'subscription_id', data_type: 'string', required: false },
          { name: 'receipt_url', data_type: 'string', required: false },
          { name: 'created_at', data_type: 'datetime', required: true },
          { name: 'refunded_amount', data_type: 'float', required: false },
        ],
        column_roles: { customer_key: 'customer_email', event_type: 'status', event_value: 'amount', event_timestamp: 'created_at' },
      },
    ],
  },
  ...CAMPAIGN_API_SOURCE_TEMPLATES,
};


// ==================== HELPER: Simple string similarity ====================
const stringSimilarity = (a, b) => {
  if (!a || !b) return 0;
  const s1 = a.toLowerCase().replace(/[_\-\s]/g, '');
  const s2 = b.toLowerCase().replace(/[_\-\s]/g, '');
  if (s1 === s2) return 1.0;
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;
  // Simple character-level bigram overlap
  const bigrams = (str) => {
    const result = new Set();
    for (let i = 0; i < str.length - 1; i++) result.add(str.slice(i, i + 2));
    return result;
  };
  const b1 = bigrams(s1), b2 = bigrams(s2);
  const intersection = [...b1].filter(x => b2.has(x)).length;
  const union = new Set([...b1, ...b2]).size;
  return union === 0 ? 0 : intersection / union;
};


// ==================== MAIN COMPONENT ====================

const APISourcesTab = ({ session, onRefresh }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedSource, setExpandedSource] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [merging, setMerging] = useState({});
  const [createdCredentials, setCreatedCredentials] = useState(null);

  useEffect(() => { loadSources(); }, [session?.session_id]);

  const loadSources = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || []);
      }
    } catch (error) {
      console.error('Error loading API sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Delete this API source? All pending data in the sink will be lost.')) return;
    try {
      const token = getCurrentToken();
      await apiCall(`/api/cdp/sessions/${session.session_id}/api-sources/${sourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadSources();
    } catch (error) {
      console.error('Error deleting source:', error);
    }
  };

  const handleToggleStatus = async (source) => {
    const newStatus = source.status === 'active' ? 'paused' : 'active';
    try {
      const token = getCurrentToken();
      await apiCall(`/api/cdp/sessions/${session.session_id}/api-sources/${source.source_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      loadSources();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleTriggerMerge = async (sourceId) => {
    try {
      setMerging(prev => ({ ...prev, [sourceId]: true }));
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources/${sourceId}/merge`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setTimeout(() => loadSources(), 3000);
      }
    } catch (error) {
      console.error('Error triggering merge:', error);
    } finally {
      setTimeout(() => setMerging(prev => ({ ...prev, [sourceId]: false })), 2000);
    }
  };

  const handlePreviewSink = async (sourceId) => {
    try {
      setPreviewLoading(true);
      setPreviewData(null);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources/${sourceId}/preview?rows=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) setPreviewData(await response.json());
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleViewPending = async (sourceId) => {
    try {
      setPendingLoading(true);
      setPendingData(null);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources/${sourceId}/pending?limit=30`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) setPendingData(await response.json());
    } catch (error) {
      console.error('Error loading pending:', error);
    } finally {
      setPendingLoading(false);
    }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); };

  const getStatusColor = (status) => {
    const map = { active: 'bg-green-100 text-green-800', paused: 'bg-yellow-100 text-yellow-800', disabled: 'bg-red-100 text-red-800' };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  // Determine CDP state
  const cdpState = useMemo(() => {
    if (!session) return 'loading';
    if (session.unified_table_name && session.current_version > 0) return 'versioned';
    return 'initial';
  }, [session]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">API Data Sources</h2>
              <p className="text-sm text-gray-600 mt-1">
                Receive real-time data via API endpoints. Data lands in a sink table and merges into your unified CDP.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {cdpState === 'versioned' && (
                <span className="flex items-center space-x-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  <Layers className="h-3 w-3" />
                  <span>v{session.current_version} schema active</span>
                </span>
              )}
              {cdpState === 'initial' && (
                <span className="flex items-center space-x-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  <Info className="h-3 w-3" />
                  <span>No unified schema yet</span>
                </span>
              )}
              <button onClick={loadSources} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Refresh">
                <RefreshCw className="h-4 w-4" />
              </button>
              <button onClick={() => setShowCreateModal(true)} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Plus className="h-4 w-4" />
                <span>New API Source</span>
              </button>
            </div>
          </div>
        </div>

        {sources.length > 0 && (
          <div className="px-6 py-3 border-b bg-gray-50 grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{sources.length}</p>
              <p className="text-xs text-gray-500">Sources</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{sources.filter(s => s.status === 'active').length}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{sources.reduce((sum, s) => sum + (s.total_records_received || 0), 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Received</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{sources.reduce((sum, s) => sum + (s.pending_records || 0), 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Pending</p>
            </div>
          </div>
        )}
      </div>

      {/* Sources List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : sources.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Zap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700">No API sources configured</p>
          <p className="text-sm text-gray-500 mt-2">Create an API source to start receiving data in real-time</p>
          <button onClick={() => setShowCreateModal(true)} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Create Your First API Source
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sources.map((source) => (
            <SourceCard
              key={source.source_id}
              source={source}
              session={session}
              expanded={expandedSource === source.source_id}
              onToggleExpand={() => {
                setExpandedSource(expandedSource === source.source_id ? null : source.source_id);
                setPreviewData(null);
                setPendingData(null);
              }}
              onDelete={() => handleDeleteSource(source.source_id)}
              onToggleStatus={() => handleToggleStatus(source)}
              onTriggerMerge={() => handleTriggerMerge(source.source_id)}
              onPreview={() => handlePreviewSink(source.source_id)}
              onViewPending={() => handleViewPending(source.source_id)}
              onRefreshSources={loadSources}
              merging={merging[source.source_id]}
              previewData={expandedSource === source.source_id ? previewData : null}
              previewLoading={previewLoading}
              pendingData={expandedSource === source.source_id ? pendingData : null}
              pendingLoading={pendingLoading}
              copyToClipboard={copyToClipboard}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateModal
          session={session}
          cdpState={cdpState}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(creds) => { setShowCreateModal(false); setCreatedCredentials(creds); loadSources(); }}
        />
      )}

      {createdCredentials && (
        <CredentialsModal credentials={createdCredentials} onClose={() => setCreatedCredentials(null)} copyToClipboard={copyToClipboard} />
      )}
    </div>
  );
};


// ==================== Source Card (unchanged core) ====================

const SourceCard = ({
  source, session, expanded, onToggleExpand, onDelete, onToggleStatus,
  onTriggerMerge, onPreview, onViewPending, onRefreshSources, merging,
  previewData, previewLoading, pendingData, pendingLoading,
  copyToClipboard, getStatusColor
}) => {
  const [tab, setTab] = useState('overview');

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 cursor-pointer hover:bg-gray-50" onClick={onToggleExpand}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {expanded ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
            <Zap className={`h-5 w-5 ${source.status === 'active' ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <h3 className="font-semibold text-gray-900">{source.source_name}</h3>
              {source.description && <p className="text-xs text-gray-500 mt-0.5">{source.description}</p>}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>{(source.total_records_received || 0).toLocaleString()} received</span>
              <span className="text-orange-600 font-medium">{(source.pending_records || 0).toLocaleString()} pending</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${source.merge_mode === 'auto' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                {source.merge_mode}
              </span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(source.status)}`}>{source.status}</span>
            <div className="flex items-center space-x-1" onClick={e => e.stopPropagation()}>
              <button onClick={onToggleStatus} className="p-1.5 hover:bg-gray-100 rounded" title={source.status === 'active' ? 'Pause' : 'Activate'}>
                {source.status === 'active' ? <Pause className="h-4 w-4 text-yellow-600" /> : <Play className="h-4 w-4 text-green-600" />}
              </button>
              {source.merge_mode === 'manual' && (
                <button onClick={onTriggerMerge} disabled={merging || !(source.pending_records > 0)} className="p-1.5 hover:bg-blue-50 rounded disabled:opacity-50" title="Merge pending">
                  <ArrowRight className={`h-4 w-4 text-blue-600 ${merging ? 'animate-pulse' : ''}`} />
                </button>
              )}
              <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded" title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></button>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t">
          <div className="px-6 py-2 border-b flex space-x-4 bg-gray-50">
            {['overview', 'credentials', 'schema', 'mapping', 'preview', 'pending'].map(t => (
              <button key={t} onClick={() => { setTab(t); if (t === 'preview') onPreview(); if (t === 'pending') onViewPending(); }}
                className={`px-3 py-1.5 text-xs font-medium rounded ${tab === t ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t === 'mapping' ? 'Column Mapping' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div className="p-6">
            {tab === 'overview' && (
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Row label="Source ID" value={source.source_id} mono />
                  <Row label="Merge Mode" value={source.merge_mode === 'auto' ? `Auto (every ${source.merge_interval_seconds}s)` : 'Manual'} />
                  <Row label="Rate Limit" value={`${source.rate_limit_per_minute}/min`} />
                  <Row label="Created" value={source.created_at ? new Date(source.created_at).toLocaleString() : '-'} />
                </div>
                <div className="space-y-3">
                  <Row label="Total Received" value={(source.total_records_received || 0).toLocaleString()} />
                  <Row label="Total Merged" value={(source.total_records_merged || 0).toLocaleString()} />
                  <Row label="Pending" value={(source.pending_records || 0).toLocaleString()} highlight />
                  <Row label="Last Received" value={source.last_received_at ? new Date(source.last_received_at).toLocaleString() : 'Never'} />
                  <Row label="Last Merged" value={source.last_merged_at ? new Date(source.last_merged_at).toLocaleString() : 'Never'} />
                </div>
              </div>
            )}

            {tab === 'credentials' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                  <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-800">The API secret was shown only at creation. If lost, regenerate from session settings.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">API Key</label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono break-all">{source.api_key}</code>
                    <button onClick={() => copyToClipboard(source.api_key)} className="p-2 hover:bg-gray-100 rounded"><Copy className="h-4 w-4 text-gray-500" /></button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Ingest Endpoint</label>
                  <code className="block px-3 py-2 bg-gray-100 rounded text-sm font-mono">POST /api/cdp/ingest/{source.api_key}</code>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Example cURL</label>
                  <div className="bg-gray-900 rounded-lg p-4 text-xs text-green-400 font-mono overflow-x-auto whitespace-pre">{`curl -X POST "YOUR_HOST/api/cdp/ingest/${source.api_key}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Secret: YOUR_SECRET" \\
  -d '{"records": [{${(source.column_schema || []).map(c => `"${c.name}":"val"`).join(', ')}}]}'`}</div>
                </div>
              </div>
            )}

            {tab === 'schema' && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Column Schema ({(source.column_schema || []).length} columns)</h4>
                  {(source.primary_key_columns || []).length > 0 && (
                    <span className="text-xs text-gray-500">PKs: {source.primary_key_columns.join(', ')}</span>
                  )}
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Column</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Required</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Key</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(source.column_schema || []).map((col, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 font-mono text-sm">{col.name}</td>
                          <td className="px-4 py-2 text-gray-600">{col.data_type || 'string'}</td>
                          <td className="px-4 py-2">{col.required ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span className="text-gray-300">—</span>}</td>
                          <td className="px-4 py-2">{(source.primary_key_columns || []).includes(col.name) ? <Key className="h-4 w-4 text-blue-500" /> : <span className="text-gray-300">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'mapping' && (
              <APISourceMappingEditor source={source} session={session} onMappingSaved={() => onRefreshSources && onRefreshSources()} />
            )}

            {tab === 'preview' && <DataTable data={previewData} loading={previewLoading} title="Latest Sink Records" onRefresh={onPreview} />}
            {tab === 'pending' && (
              <div>
                <DataTable data={pendingData} loading={pendingLoading} title={`Pending Records (${(pendingData?.total_pending || 0).toLocaleString()} total)`} onRefresh={onViewPending} />
                {pendingData?.total_pending > 0 && source.merge_mode === 'manual' && (
                  <div className="mt-4 flex justify-end">
                    <button onClick={onTriggerMerge} disabled={merging} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2">
                      <ArrowRight className="h-4 w-4" />
                      <span>Merge All Pending</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== API Source Column Mapping Editor ====================

const formatChType = (chType) => {
  if (!chType) return 'Text';
  const clean = chType.replace(/^Nullable\(/, '').replace(/\)$/, '');
  const map = {
    'String': 'Text', 'Int64': 'Integer', 'Int32': 'Integer', 'UInt8': 'Boolean',
    'Float64': 'Decimal', 'Float32': 'Decimal', 'DateTime64(3)': 'DateTime',
    'Date': 'Date', 'Date32': 'Date',
  };
  return map[clean] || clean;
};

const APISourceMappingEditor = ({ source, session, onMappingSaved }) => {
  const [unifiedCols, setUnifiedCols] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const sourceColumns = source.column_schema || [];
  const pkColumns = source.primary_key_columns || [];

  useEffect(() => { fetchUnifiedCols(); }, [session?.session_id]);

  const fetchUnifiedCols = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentToken();
      // Try /unified-schema first (returns {columns: [{name, data_type, ...}]})
      let cols = [];
      const r1 = await apiCall(`/api/cdp/sessions/${session.session_id}/unified-schema`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r1.ok) {
        const d = await r1.json();
        cols = (d.columns || []).filter(c => !c.name.startsWith('_')).map(c => ({ name: c.name, type: c.data_type || 'String' }));
      }
      // Fallback to /schema if no columns
      if (cols.length === 0) {
        const r2 = await apiCall(`/api/cdp/sessions/${session.session_id}/schema`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (r2.ok) {
          const d2 = await r2.json();
          cols = Object.entries(d2.columns || {}).filter(([k]) => !k.startsWith('_')).map(([name, type]) => ({ name, type }));
        }
      }
      // Fallback: query ClickHouse DESCRIBE directly
      if (cols.length === 0 && session.unified_table_name) {
        const r3 = await apiCall(`/api/cdp/sessions/${session.session_id}/query`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: `DESCRIBE TABLE ${session.unified_table_name}` })
        });
        if (r3.ok) {
          const d3 = await r3.json();
          const rows = d3.data || d3.rows || [];
          cols = rows.filter(r => !r[0]?.startsWith('_') && !['_source_file','_upload_id','_row_hash','_merged','_created_at'].includes(r[0]))
                      .map(r => ({ name: r[0], type: r[1] || 'String' }));
        }
      }
      setUnifiedCols(cols);
      initMappings(cols);
    } catch (e) {
      setError('Failed to load unified schema: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const initMappings = (uCols) => {
    const stored = source.column_mappings || {};
    const uNames = uCols.map(c => c.name);
    const init = {};
    for (const col of sourceColumns) {
      const s = col.name;
      if (stored[s] && uNames.includes(stored[s])) {
        init[s] = stored[s];
      } else if (uNames.includes(s)) {
        init[s] = s;
      } else {
        init[s] = '__new__';
      }
    }
    setMappings(init);
    setDirty(false);
  };

  const setMapping = (srcCol, target) => {
    setMappings(prev => ({ ...prev, [srcCol]: target }));
    setDirty(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    const resolved = {};
    const newColumns = [];
    for (const col of sourceColumns) {
      const s = col.name;
      const t = mappings[s];
      if (!t || t === '__new__') {
        resolved[s] = s;
        newColumns.push({ name: s, data_type: col.data_type || 'string' });
      } else {
        resolved[s] = t;
      }
    }
    try {
      setSaving(true);
      const token = getCurrentToken();
      const resp = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources/${source.source_id}/mappings`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ column_mappings: resolved, new_columns: newColumns }),
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        setSuccess(`Saved. ${data.columns_added || 0} new column(s) added.`);
        setDirty(false);
        if (onMappingSaved) onMappingSaved(resolved);
      } else {
        const err = await resp.json();
        setError(err.detail || 'Failed to save');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-sm text-gray-600">Loading unified columns...</span>
      </div>
    );
  }

  if (unifiedCols.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
        <Info className="h-6 w-6 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-amber-800">No unified table columns found. Unify your CDP data first, then map API source columns here.</p>
      </div>
    );
  }

  const mapped = sourceColumns.filter(c => mappings[c.name] && mappings[c.name] !== '__new__' && unifiedCols.some(uc => uc.name === mappings[c.name])).length;
  const newCount = sourceColumns.length - mapped;

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      <div className="flex items-center space-x-3 text-xs">
        <span className="text-gray-500">{sourceColumns.length} source columns →</span>
        <span className="flex items-center space-x-1 px-2 py-1 bg-green-50 text-green-700 rounded-full font-medium">
          <Check className="h-3 w-3" /><span>{mapped} mapped</span>
        </span>
        <span className="flex items-center space-x-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
          <Plus className="h-3 w-3" /><span>{newCount} new</span>
        </span>
        <span className="text-gray-400 ml-auto">{unifiedCols.length} unified columns available</span>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">API Source Column</th>
              <th className="px-2 py-2.5 text-center w-10"></th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Maps To (Unified Table)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sourceColumns.map((col) => {
              const src = col.name;
              const target = mappings[src] || '__new__';
              const isMapped = target !== '__new__' && unifiedCols.some(uc => uc.name === target);
              const isExact = isMapped && target === src;
              const isPK = pkColumns.includes(src);

              return (
                <tr key={src} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      {isPK && <Key className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" title="Primary Key" />}
                      <div>
                        <span className="text-sm font-mono font-medium text-gray-900">{src}</span>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <span className="text-[10px] text-gray-400">{col.data_type || 'string'}</span>
                          {col.required && <span className="text-[10px] text-red-400 font-medium">required</span>}
                          {isPK && <span className="text-[10px] text-blue-500 font-medium">PK</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <ArrowRight className={`h-4 w-4 mx-auto ${isExact ? 'text-green-500' : isMapped ? 'text-yellow-500' : 'text-blue-400'}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <select
                        value={target}
                        onChange={(e) => setMapping(src, e.target.value)}
                        className={`flex-1 px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isExact ? 'border-green-300 bg-green-50 text-green-900 font-medium' :
                          isMapped ? 'border-yellow-300 bg-yellow-50 text-yellow-900' :
                          'border-blue-300 bg-blue-50 text-blue-900'
                        }`}
                      >
                        <option value="__new__">+ New Column (will create &quot;{src}&quot;)</option>
                        {unifiedCols.map(uc => (
                          <option key={uc.name} value={uc.name}>{uc.name} ({formatChType(uc.type)})</option>
                        ))}
                      </select>
                      {isExact && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium whitespace-nowrap">Exact</span>}
                      {isMapped && !isExact && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-medium whitespace-nowrap">Mapped</span>}
                      {!isMapped && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium whitespace-nowrap">New</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs">
          {dirty ? <span className="text-blue-700 font-medium">Unsaved changes</span>
           : success ? <span className="text-green-700">✓ Mappings saved</span>
           : <span className="text-gray-400">Change mappings using the dropdowns above</span>}
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={() => { initMappings(unifiedCols); setSuccess(null); setError(null); }}
            disabled={saving || !dirty} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 disabled:opacity-50">Reset</button>
          <button onClick={handleSave} disabled={saving || !dirty}
            className="px-4 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1.5">
            {saving ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div><span>Saving...</span></>)
              : (<><CheckCircle className="h-3.5 w-3.5" /><span>Save Mappings</span></>)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Shared UI ====================

const Row = ({ label, value, mono, highlight }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-gray-500">{label}</span>
    <span className={`text-sm ${mono ? 'font-mono text-xs' : ''} ${highlight ? 'font-bold text-orange-600' : 'text-gray-900'}`}>{value}</span>
  </div>
);

const DataTable = ({ data, loading, title, onRefresh }) => {
  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
      <span className="text-sm text-gray-600">Loading...</span>
    </div>
  );
  if (!data?.records?.length) return (
    <div className="text-center py-8 text-gray-500">
      <Database className="h-8 w-8 mx-auto mb-2 text-gray-300" />
      <p className="text-sm">No records found</p>
    </div>
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <button onClick={onRefresh} className="p-1 hover:bg-gray-100 rounded"><RefreshCw className="h-3.5 w-3.5 text-gray-400" /></button>
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              {data.columns.map((col, i) => (
                <th key={i} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.records.map((rec, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {data.columns.map((col, ci) => (
                  <td key={ci} className="px-3 py-1.5 text-gray-700 whitespace-nowrap max-w-xs truncate">
                    {rec[col] == null ? <span className="text-gray-300 italic">null</span> : String(rec[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ==================== CREATE MODAL (completely rebuilt) ====================

const CreateModal = ({
  session,
  cdpState,
  onClose,
  onSuccess,
  sourceFilter = 'all',        // 'all' | 'campaign' | 'customer'
  defaultCampaignId = null,    // pre-fills linkedCampaignId
}) => {
  // Wizard step: 'method' → 'template_browse' → 'configure' → ('classify' for campaign)
  // Skip method picker when filtered (user came from Campaigns tab)
  const [step, setStep] = useState(sourceFilter !== 'all' ? 'template_browse' : 'method');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Campaign-flow classification state
  const [detectedClassification, setDetectedClassification] = useState(null);
  const [detectingClassification, setDetectingClassification] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mergeMode, setMergeMode] = useState('auto');
  const [mergeInterval, setMergeInterval] = useState(300);
  const [rateLimit, setRateLimit] = useState(1000);
  const [columns, setColumns] = useState([{ name: '', data_type: 'string', required: false }]);
  const [primaryKeys, setPrimaryKeys] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [dataIntent, setDataIntent] = useState('customer_profile');
  const [linkedCampaignId, setLinkedCampaignId] = useState('');
  const [columnRoles, setColumnRoles] = useState({});

  // If parent passed a campaign to pre-link to, seed it once on mount.
  useEffect(() => {
    if (defaultCampaignId) {
      setLinkedCampaignId(defaultCampaignId);
    }
  }, [defaultCampaignId]);

  // Column mapping for versioned CDP
  const [columnMappings, setColumnMappings] = useState({}); // sourceCol → { target: 'existing_col' | '__new__', newName: '' }
  const [unifiedColumns, setUnifiedColumns] = useState([]); // [{name, type}]
  const [loadingSchema, setLoadingSchema] = useState(false);

  // Template search
  const [templateSearch, setTemplateSearch] = useState('');

  // Load unified schema if versioned
  useEffect(() => {
    if (cdpState === 'versioned' && session?.session_id) {
      loadUnifiedColumnsForMapping();
    }
  }, [cdpState, session?.session_id]);

  const loadUnifiedColumnsForMapping = async () => {
    try {
      setLoadingSchema(true);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/schema`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const cols = data.columns
          ? Object.entries(data.columns)
              .filter(([k]) => !k.startsWith('_'))
              .map(([name, type]) => ({ name, type }))
          : [];
        setUnifiedColumns(cols);
      }
    } catch (e) {
      console.error('Error loading unified schema:', e);
    } finally {
      setLoadingSchema(false);
    }
  };

  // When a template is selected, populate form
  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setName(template.name);
    setDescription(template.description);
    setMergeMode(template.merge_mode || 'auto');
    setMergeInterval(template.merge_interval_seconds || 300);
    setRateLimit(template.rate_limit_per_minute || 1000);
    setDataIntent(template.data_intent || 'customer_profile');
    setColumnRoles(template.column_roles || {});
    setPrimaryKeys(template.primary_key_columns || []);

    const templateCols = template.column_schema.map(c => ({
      name: c.name,
      data_type: c.data_type || 'string',
      required: c.required || false,
      description: c.description || '',
    }));
    setColumns(templateCols);

    // Auto-compute column mappings for versioned CDP
    if (cdpState === 'versioned' && unifiedColumns.length > 0) {
      const mappings = {};
      for (const col of templateCols) {
        const exactMatch = unifiedColumns.find(uc => uc.name === col.name);
        if (exactMatch) {
          mappings[col.name] = { target: col.name, action: 'map' };
        } else {
          // Try fuzzy match
          let best = null, bestScore = 0;
          for (const uc of unifiedColumns) {
            const score = stringSimilarity(col.name, uc.name);
            if (score > bestScore && score >= 0.65) { bestScore = score; best = uc.name; }
          }
          if (best) {
            mappings[col.name] = { target: best, action: 'map', confidence: bestScore };
          } else {
            mappings[col.name] = { target: '__new__', action: 'new' };
          }
        }
      }
      setColumnMappings(mappings);
    }

    setStep('configure');
  };

  // Column helpers
  const addColumn = () => setColumns([...columns, { name: '', data_type: 'string', required: false }]);
  const removeColumn = (idx) => {
    const removed = columns[idx].name;
    setColumns(columns.filter((_, i) => i !== idx));
    setPrimaryKeys(primaryKeys.filter(pk => pk !== removed));
    const newMappings = { ...columnMappings };
    delete newMappings[removed];
    setColumnMappings(newMappings);
  };
  const updateColumn = (idx, field, value) => {
    const updated = [...columns];
    const oldName = updated[idx].name;
    updated[idx] = { ...updated[idx], [field]: value };
    setColumns(updated);
    if (field === 'name') {
      if (primaryKeys.includes(oldName)) {
        setPrimaryKeys(primaryKeys.map(pk => pk === oldName ? value : pk));
      }
      // Update mapping key
      if (oldName in columnMappings) {
        const newMappings = { ...columnMappings };
        newMappings[value] = newMappings[oldName];
        delete newMappings[oldName];
        setColumnMappings(newMappings);
      }
    }
  };
  const togglePK = (colName) => {
    setPrimaryKeys(prev => prev.includes(colName) ? prev.filter(pk => pk !== colName) : [...prev, colName]);
  };

  // Set mapping for a column
  const setMapping = (sourceCol, target) => {
    setColumnMappings(prev => ({
      ...prev,
      [sourceCol]: target === '__new__'
        ? { target: '__new__', action: 'new' }
        : { target, action: 'map' },
    }));
  };

  // Initialize mappings when columns change (for manual mode)
  useEffect(() => {
    if (cdpState === 'versioned' && unifiedColumns.length > 0 && step === 'configure') {
      const mappings = { ...columnMappings };
      for (const col of columns) {
        if (col.name && !(col.name in mappings)) {
          const exactMatch = unifiedColumns.find(uc => uc.name === col.name);
          if (exactMatch) {
            mappings[col.name] = { target: col.name, action: 'map' };
          } else {
            mappings[col.name] = { target: '__new__', action: 'new' };
          }
        }
      }
      setColumnMappings(mappings);
    }
  }, [columns, unifiedColumns, step]);

  // When the user is on the campaign flow, we don't submit straight from
  // 'configure' — we first run classify/auto-detect on the declared schema
  // and show the ClassificationMappingEditor so mappings are explicit. The
  // editor's onConfirm calls handleSubmitWithClassification which merges
  // its output into state and invokes handleSubmit.
  const runAutoDetectAndAdvance = async () => {
    setError(null);
    const validCols = columns.filter(c => c.name.trim());
    if (!name.trim()) { setError('Source name is required'); return; }
    if (!validCols.length) { setError('At least one column is required'); return; }
    
    try {
      setDetectingClassification(true);
      const resp = await apiCall(
        `/api/cdp/sessions/${session.session_id}/classify/auto-detect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source_type: 'api',
            source_id: 'pending',
            columns: validCols.map(c => c.name),
          }),
        }
      );
      const detect = await resp.json();
      
      // If the user picked a vendor template, its column_roles are already
      // expert-authored — they beat whatever auto-detect infers on field
      // names alone. Use template's roles as the starting point, fill any
      // gaps from auto-detect.
      const templateRoles = selectedTemplate?.column_roles || {};
      const mergedRoles = {
        ...(detect.column_roles || {}),
        ...Object.fromEntries(Object.entries(templateRoles).filter(([_, v]) => v)),
      };
      
      const effectiveIntent = selectedTemplate?.data_intent
        && ['campaign_response', 'campaign_conversion'].includes(selectedTemplate.data_intent)
        ? selectedTemplate.data_intent
        : (detect.intent && detect.intent !== 'customer_profile'
            ? detect.intent
            : 'campaign_response');
      
      setDetectedClassification({
        ...detect,
        intent: effectiveIntent,
        column_roles: mergedRoles,
        // Boost confidence if template gave us roles — the template is human-authored
        confidence: templateRoles && Object.keys(templateRoles).length >= 3
          ? Math.max(detect.confidence || 0, 0.9)
          : (detect.confidence || 0),
      });
      setStep('classify');
    } catch (e) {
      console.error('Auto-detect failed:', e);
      setError('Could not auto-detect — you can still fill mappings manually');
      // Fall back to showing the editor with empty suggestions
      setDetectedClassification({
        intent: selectedTemplate?.data_intent || 'campaign_response',
        column_roles: selectedTemplate?.column_roles || {},
        confidence: 0,
        matched_columns: [],
      });
      setStep('classify');
    } finally {
      setDetectingClassification(false);
    }
  };
  
  const handleClassifyConfirm = ({ intent, column_roles, metric_definitions }) => {
    // Update state, then submit. Because setState is async, we pass the
    // values directly to a submit variant that doesn't re-read state.
    setDataIntent(intent);
    setColumnRoles(column_roles);
    handleSubmit({
      overrideIntent: intent,
      overrideRoles: column_roles,
      overrideMetrics: metric_definitions,
    });
  };

  const handleSubmit = async (overrides = {}) => {
    setError(null);
    if (!name.trim()) { setError('Source name is required'); return; }
    const validCols = columns.filter(c => c.name.trim());
    if (!validCols.length) { setError('At least one column is required'); return; }
    const names = validCols.map(c => c.name);
    if (new Set(names).size !== names.length) { setError('Duplicate column names'); return; }

    // For versioned CDP, verify at least one key column is mapped
    if (cdpState === 'versioned' && primaryKeys.length === 0) {
      setError('At least one primary key column is required for merge identification');
      return;
    }

    try {
      setCreating(true);
      const token = getCurrentToken();

      // Build the payload with mapping metadata
      // When called from the classify step, we pass explicit overrides so we
      // don't depend on setState having flushed before the fetch fires.
      const effectiveIntent = overrides.overrideIntent ?? dataIntent;
      const effectiveRoles  = overrides.overrideRoles  ?? columnRoles;
      const effectiveMetrics = overrides.overrideMetrics ?? [];
      
      const payload = {
        session_id: session.session_id,
        source_name: name.trim(),
        description: description.trim() || null,
        column_schema: validCols.map(c => ({
          name: c.name,
          data_type: c.data_type,
          required: c.required,
        })),
        merge_mode: mergeMode,
        primary_key_columns: primaryKeys.length ? primaryKeys : null,
        merge_interval_seconds: mergeInterval,
        rate_limit_per_minute: rateLimit,
        data_intent: effectiveIntent,
        linked_campaign_id: linkedCampaignId || null,
        column_roles: Object.fromEntries(Object.entries(effectiveRoles).filter(([, v]) => v)),
        metric_definitions: effectiveMetrics,
      };

      // Include column mapping metadata for versioned CDP
      if (cdpState === 'versioned') {
        payload.column_mappings = columnMappings;
      }

      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/api-sources/create`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        onSuccess(await response.json());
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to create API source');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  // Method selection step
  const renderMethodStep = () => (
    <div className="px-6 py-6 space-y-4">
      <p className="text-sm text-gray-600">How would you like to set up your API source?</p>

      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setStep('template_browse')}
          className="border-2 border-gray-200 hover:border-blue-400 rounded-xl p-5 text-left transition-all hover:shadow-md group"
        >
          <LayoutTemplate className="h-8 w-8 text-blue-500 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-gray-900 mb-1">Use a Template</h3>
          <p className="text-xs text-gray-500">
            Pre-built schemas for Salesforce, Zendesk, Shopify, GA4, Stripe and more.
            {cdpState === 'versioned' && ' Columns auto-map to your existing schema.'}
          </p>
        </button>

        <button
          onClick={() => {
            setColumns([{ name: '', data_type: 'string', required: false }]);
            setPrimaryKeys([]);
            setStep('configure');
          }}
          className="border-2 border-gray-200 hover:border-blue-400 rounded-xl p-5 text-left transition-all hover:shadow-md group"
        >
          <Settings className="h-8 w-8 text-gray-500 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-semibold text-gray-900 mb-1">Custom Schema</h3>
          <p className="text-xs text-gray-500">
            Define your own column schema manually.
            {cdpState === 'versioned' && ' Map each column to existing unified columns or add new ones.'}
          </p>
        </button>
      </div>

      {cdpState === 'versioned' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
          <Layers className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800">
            Your CDP has a unified schema (v{session.current_version}) with {unifiedColumns.length} columns.
            API source columns will be mapped to existing columns where possible, or added as new columns.
          </p>
        </div>
      )}

      {cdpState === 'initial' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
          <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            Your CDP has no unified data yet. API source columns will define the initial schema when data is first ingested and merged.
            A primary key column is mandatory for merge identification.
          </p>
        </div>
      )}
    </div>
  );

  // Template browsing step
  const renderTemplateBrowseStep = () => {
    const filtered = {};
    for (const [catKey, cat] of Object.entries(API_SOURCE_TEMPLATES)) {
      const matchingTemplates = cat.templates.filter(t => {
        // Filter by sourceFilter prop (campaign vs customer vs all)
        if (sourceFilter === 'campaign' && !isCampaignTemplate(t)) return false;
        if (sourceFilter === 'customer' && isCampaignTemplate(t)) return false;

        // Existing search filter
        return !templateSearch ||
          t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
          t.provider.toLowerCase().includes(templateSearch.toLowerCase()) ||
          t.description.toLowerCase().includes(templateSearch.toLowerCase());
      });
      if (matchingTemplates.length > 0) {
        filtered[catKey] = { ...cat, templates: matchingTemplates };
      }
    }

    return (
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => sourceFilter !== 'all' ? onClose() : setStep('method')}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            title={sourceFilter !== 'all' ? 'Close' : 'Back'}
          >
            <ChevronRight className="h-4 w-4 text-gray-400 rotate-180" />
          </button>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              placeholder="Search templates... (e.g. Salesforce, Shopify, Zendesk)"
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto space-y-6 pr-1">
          {Object.entries(filtered).map(([catKey, cat]) => (
            <div key={catKey}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center space-x-2">
                <span>{cat.icon}</span>
                <span>{cat.category}</span>
                <span className="text-gray-300">({cat.templates.length})</span>
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {cat.templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="flex items-center space-x-3 px-4 py-3 border rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left group"
                  >
                    <span className="text-xl flex-shrink-0">{template.logo}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm text-gray-900">{template.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{template.column_schema.length} cols</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{template.description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ))}

          {Object.keys(filtered).length === 0 && (
            <div className="text-center py-8">
              <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No templates match "{templateSearch}"</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Configuration step
  const renderConfigureStep = () => {
    const hasMappableColumns = cdpState === 'versioned' && unifiedColumns.length > 0;
    const validColumns = columns.filter(c => c.name.trim());

    // Count mapping statuses
    const mappedCount = validColumns.filter(c => columnMappings[c.name]?.action === 'map').length;
    const newCount = validColumns.filter(c => columnMappings[c.name]?.action === 'new' || !columnMappings[c.name]).length;

    return (
      <div className="px-6 py-4 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Template badge */}
        {selectedTemplate && (
          <div className="flex items-center space-x-2">
            <span className="text-sm">{selectedTemplate.logo}</span>
            <span className="text-xs font-medium text-gray-500">Template: {selectedTemplate.provider}</span>
            <button onClick={() => { setSelectedTemplate(null); setStep('template_browse'); }} className="text-xs text-blue-600 hover:text-blue-800">(change)</button>
          </div>
        )}

        {/* Source name & description */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., CRM Webhook, POS System" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional description" />
          </div>
        </div>

        {/* Column Schema with Mapping */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700">Column Schema *</label>
              {hasMappableColumns && (
                <span className="text-[10px] text-gray-400">
                  {mappedCount} mapped · {newCount} new
                </span>
              )}
            </div>
            <button onClick={addColumn} className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1">
              <Plus className="h-3 w-3" /><span>Add Column</span>
            </button>
          </div>

          {/* Column header */}
          {hasMappableColumns && (
            <div className="flex items-center space-x-2 mb-1 px-1">
              <span className="flex-1 text-[10px] text-gray-400 font-medium uppercase tracking-wider">Source Column</span>
              <span className="w-20 text-[10px] text-gray-400 font-medium uppercase tracking-wider">Type</span>
              <span className="w-48 text-[10px] text-gray-400 font-medium uppercase tracking-wider">Map to Unified Column</span>
              <span className="w-16"></span>
            </div>
          )}

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {columns.map((col, idx) => (
              <div key={idx} className={`flex items-center space-x-2 ${hasMappableColumns ? '' : ''}`}>
                <input type="text" value={col.name} onChange={(e) => updateColumn(idx, 'name', e.target.value.replace(/\s/g, '_'))}
                  className="flex-1 px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="column_name" />
                <select value={col.data_type} onChange={(e) => updateColumn(idx, 'data_type', e.target.value)} className="w-20 px-2 py-1.5 border rounded text-sm">
                  <option value="string">String</option>
                  <option value="int">Integer</option>
                  <option value="float">Float</option>
                  <option value="bool">Boolean</option>
                  <option value="datetime">DateTime</option>
                  <option value="date">Date</option>
                </select>

                {/* Mapping dropdown for versioned CDP */}
                {hasMappableColumns && col.name.trim() && (
                  <select
                    value={columnMappings[col.name]?.target || '__new__'}
                    onChange={(e) => setMapping(col.name, e.target.value)}
                    className={`w-48 px-2 py-1.5 border rounded text-sm ${
                      columnMappings[col.name]?.action === 'map'
                        ? 'border-green-300 bg-green-50 text-green-800'
                        : 'border-blue-300 bg-blue-50 text-blue-800'
                    }`}
                  >
                    <option value="__new__">+ New column</option>
                    <optgroup label="Existing columns">
                      {unifiedColumns.map(uc => (
                        <option key={uc.name} value={uc.name}>
                          {uc.name} ({uc.type.replace('Nullable(', '').replace(')', '')})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                )}
                {hasMappableColumns && !col.name.trim() && (
                  <div className="w-48" />
                )}

                <label className="flex items-center space-x-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={col.required} onChange={(e) => updateColumn(idx, 'required', e.target.checked)} className="rounded" />
                  <span>Req</span>
                </label>
                <button onClick={() => col.name && togglePK(col.name)} disabled={!col.name}
                  className={`p-1 rounded ${primaryKeys.includes(col.name) ? 'bg-blue-100 text-blue-600' : 'text-gray-300 hover:text-gray-500'}`} title="Primary key">
                  <Key className="h-3.5 w-3.5" />
                </button>
                {columns.length > 1 && (
                  <button onClick={() => removeColumn(idx)} className="p-1 text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
          </div>
          {primaryKeys.length > 0 && <p className="text-xs text-blue-600 mt-2">Primary keys: {primaryKeys.join(', ')}</p>}
          {primaryKeys.length === 0 && <p className="text-xs text-amber-600 mt-2">⚠ Select at least one primary key for merge identification</p>}
        </div>

        {/* Merge Config */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Merge Mode</label>
            <select value={mergeMode} onChange={(e) => setMergeMode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
              <option value="auto">Auto (merge at intervals)</option>
              <option value="manual">Manual (merge on confirmation)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">{mergeMode === 'auto' ? 'Data merges automatically at regular intervals' : 'You manually trigger merges when ready'}</p>
          </div>
          {mergeMode === 'auto' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Merge Interval</label>
              <select value={mergeInterval} onChange={(e) => setMergeInterval(parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value={60}>Every 1 minute</option>
                <option value={300}>Every 5 minutes</option>
                <option value={600}>Every 10 minutes</option>
                <option value={1800}>Every 30 minutes</option>
                <option value={3600}>Every 1 hour</option>
              </select>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (req/min)</label>
          <input type="number" value={rateLimit} onChange={(e) => setRateLimit(parseInt(e.target.value) || 1000)} className="w-full px-3 py-2 border rounded-lg text-sm" min={10} max={100000} />
        </div>

        {/* Data Classification */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Data Classification</h3>
          <p className="text-xs text-gray-500 mb-3">Declare how this data should be used. This enables automatic event extraction and campaign attribution after each merge.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Intent</label>
              <select value={dataIntent} onChange={(e) => setDataIntent(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="customer_profile">Customer Profile (default)</option>
                <option value="campaign_response">Campaign Response (delivery, opens, clicks)</option>
                <option value="campaign_conversion">Campaign Conversion (purchases, redemptions)</option>
                <option value="event_stream">Event Stream (app events, page views)</option>
                <option value="transaction">Transaction (payments, invoices)</option>
                <option value="survey_feedback">Survey / Feedback (NPS, CSAT)</option>
                <option value="enrichment">Enrichment (external data)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link to Campaign (optional)</label>
              <input type="text" value={linkedCampaignId} onChange={(e) => setLinkedCampaignId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="campaign_id" />
            </div>
          </div>

          {dataIntent !== 'customer_profile' && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-2">Column Role Mapping</label>
              <div className="grid grid-cols-2 gap-2">
                {['customer_key', 'event_type', 'event_value', 'event_timestamp'].map(role => (
                  <div key={role}>
                    <label className="block text-[11px] text-gray-400 mb-0.5">{role.replace('_', ' ')}</label>
                    <select value={columnRoles[role] || ''} onChange={(e) => setColumnRoles(prev => ({ ...prev, [role]: e.target.value }))}
                      className="w-full px-2 py-1.5 border rounded text-xs">
                      <option value="">— select column —</option>
                      {columns.filter(c => c.name.trim()).map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Map your columns to event roles so the system knows how to extract events.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {step !== 'method' && (
              <button
                onClick={() => {
                  if (step === 'classify') {
                    setStep('configure');
                  } else if (step === 'configure' && selectedTemplate) {
                    setStep('template_browse');
                  } else if (sourceFilter !== 'all') {
                    onClose();
                  } else {
                    setStep('method');
                  }
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="h-4 w-4 text-gray-400 rotate-180" />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'method' && 'Create API Data Source'}
              {step === 'template_browse' && 'Choose a Template'}
              {step === 'configure' && (selectedTemplate ? `Configure: ${selectedTemplate.name}` : 'Configure API Source')}
              {step === 'classify' && 'Confirm classification mappings'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {step === 'method' && renderMethodStep()}
        {step === 'template_browse' && renderTemplateBrowseStep()}
        {step === 'configure' && renderConfigureStep()}
        {step === 'classify' && detectedClassification && (
          <div className="p-6">
            <ClassificationMappingEditor
              filename={name || selectedTemplate?.name || 'API source'}
              columns={columns.filter(c => c.name.trim()).map(c => c.name)}
              sampleRows={[]}  /* no sample values for API sources at creation time */
              detected={detectedClassification}
              mode="api"
              onCancel={() => setStep('configure')}
              onConfirm={handleClassifyConfirm}
            />
            {creating && (
              <div className="mt-3 text-xs text-gray-500">Creating API source…</div>
            )}
            {error && (
              <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {error}
              </div>
            )}
          </div>
        )}

        {step === 'configure' && (
          <div className="px-6 py-4 border-t flex items-center justify-end space-x-3 sticky bottom-0 bg-white">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            {sourceFilter === 'campaign' ? (
              <button
                onClick={runAutoDetectAndAdvance}
                disabled={detectingClassification}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {detectingClassification ? 'Analysing columns…' : (
                  <>Next: classify mappings <ChevronRight className="h-4 w-4" /></>
                )}
              </button>
            ) : (
              <button onClick={() => handleSubmit()} disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create API Source'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== Credentials Modal ====================

const CredentialsModal = ({ credentials, onClose, copyToClipboard }) => {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const handleCopy = (text, type) => {
    copyToClipboard(text);
    const setter = type === 'key' ? setCopiedKey : setCopiedSecret;
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b bg-green-50 flex items-center space-x-3">
          <CheckCircle className="h-6 w-6 text-green-500" />
          <div>
            <h2 className="text-lg font-bold text-gray-900">API Source Created!</h2>
            <p className="text-sm text-gray-600">Save your credentials now — the secret will not be shown again.</p>
          </div>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm font-medium text-red-800">⚠️ Copy your API Secret now! It cannot be retrieved later.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">API Key</label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono break-all">{credentials.api_key}</code>
              <button onClick={() => handleCopy(credentials.api_key, 'key')} className="p-2 hover:bg-gray-100 rounded">
                {copiedKey ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-500" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">API Secret</label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm font-mono break-all">{credentials.api_secret}</code>
              <button onClick={() => handleCopy(credentials.api_secret, 'secret')} className="p-2 hover:bg-gray-100 rounded">
                {copiedSecret ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-500" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Ingest Endpoint</label>
            <code className="block px-3 py-2 bg-gray-100 rounded text-sm font-mono">POST /api/cdp/ingest/{credentials.api_key}</code>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">I've Saved My Credentials</button>
        </div>
      </div>
    </div>
  );
};

export default APISourcesTab;
export { CreateModal };